import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { PRODUCTS } from "@/data/content";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Send } from "lucide-react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useContent } from "@/hooks/useContent";
import { resolveMedia } from "@/lib/media";

const emptyForm = { name: "", email: "", company: "", phone: "", quantity: "", message: "" };

const Products = () => {
  useDocumentTitle("Products");
  const { items: PROD } = useContent("products", PRODUCTS);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");

  const categories = ["All", ...Array.from(new Set(PROD.map((p) => p.category).filter(Boolean)))];
  const visible = filter === "All" ? PROD : PROD.filter((p) => p.category === filter);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/products/enquiry", { ...form, product: selected.name });
      toast.success("Enquiry submitted. Our team will get back to you.");
      setForm(emptyForm);
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="products-page">
      <PageHeader
        eyebrow="Products"
        title="Precision engineered. Field-proven."
        description="Explore our current product families across motors, controllers, embedded devices, AI systems and scientific instruments."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Products" }]}
      />

      <section className="py-14 lg:py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category chips */}
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`product-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-xs uppercase tracking-widest px-3.5 py-2 rounded-full border transition-colors ${
                  filter === c
                    ? "bg-[#0B1B3D] text-white border-[#0B1B3D]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1B3D]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p) => (
              <div
                key={p.id || p.slug}
                data-testid={`product-card-${p.slug}`}
                className="group bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={resolveMedia(p.image_url || p.image)}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-white/90 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full">
                    {p.category}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-semibold text-[#0B1B3D] leading-snug">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {p.description}
                  </p>

                  <div className="mt-4 border-t border-slate-100 pt-4 grid gap-1.5">
                    {Object.entries(p.specs || {}).slice(0, 3).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 uppercase tracking-wider">
                          {k}
                        </span>
                        <span className="font-mono-akyvia text-[#0B1B3D] font-medium">
                          {v}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {(p.applications || []).slice(0, 2).map((a) => (
                        <span
                          key={a}
                          className="text-[10px] uppercase tracking-widest text-slate-500 border border-slate-200 rounded-full px-2 py-0.5"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                    <button
                      data-testid={`product-enquire-${p.slug}`}
                      onClick={() => setSelected(p)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0B1B3D] hover:text-amber-600 transition-colors"
                    >
                      Enquire
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="product-enquiry-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0B1B3D]">
              Enquire · {selected?.name}
            </DialogTitle>
            <DialogDescription>
              Our engineering team will respond within one business day.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="enq-name">Name*</Label>
                <Input
                  id="enq-name"
                  data-testid="enquiry-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="enq-email">Email*</Label>
                <Input
                  id="enq-email"
                  type="email"
                  data-testid="enquiry-email-input"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="enq-company">Company</Label>
                <Input
                  id="enq-company"
                  data-testid="enquiry-company-input"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="enq-phone">Phone</Label>
                <Input
                  id="enq-phone"
                  data-testid="enquiry-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="enq-qty">Quantity / Application</Label>
                <Input
                  id="enq-qty"
                  data-testid="enquiry-quantity-input"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="enq-msg">Message</Label>
                <Textarea
                  id="enq-msg"
                  data-testid="enquiry-message-input"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={loading}
                data-testid="enquiry-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] disabled:opacity-60 text-white px-5 py-3 rounded-md font-medium w-full sm:w-auto"
              >
                <Send className="w-4 h-4" />
                {loading ? "Sending..." : "Send Enquiry"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CTASection
        eyebrow="Custom Products"
        title="Need a custom motor, controller or system?"
        description="Share your specs. We'll engineer, prototype and manufacture to your requirements."
      />
      <div className="py-24" />
    </div>
  );
};

export default Products;
